import React, { useEffect, useState, useCallback } from "react";
import api, { errMsg } from "../api";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { ShieldCheck, AlertTriangle, ExternalLink, RefreshCw, Copy, CheckCircle2, XCircle } from "lucide-react";

const REQUIRED_APP_ROLES = ["Mail.Read", "Mail.ReadWrite", "Mail.Send"];

/**
 * MicrosoftSetupChecker — Interactive Azure AD setup helper for admins.
 * Shows the current configuration state, lists missing permissions, and
 * provides a one-click admin-consent button.
 */
export default function MicrosoftSetupChecker({ lang = "de" }) {
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(false);
    const t = (de, en) => (lang === "de" ? de : en);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/ms/setup-status");
            setStatus(data);
        } catch (e) {
            toast.error(errMsg(e));
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const copy = (txt) => { navigator.clipboard.writeText(txt); toast.success(t("Kopiert", "Copied")); };

    if (!status) {
        return <div className="p-4 text-sm text-neutral-500">{t("Lade…", "Loading…")}</div>;
    }

    const missing = REQUIRED_APP_ROLES.filter((r) => !(status.app_roles || []).includes(r));
    const fullyOk = missing.length === 0 && status.test_graph_call?.ok;

    return (
        <div className="space-y-3" data-testid="ms-setup-checker">
            <div className="flex items-center justify-between">
                <h3 className="font-semibold flex items-center gap-2">
                    <ShieldCheck className="w-4 h-4" /> {t("Azure-AD Setup-Status", "Azure AD setup status")}
                </h3>
                <Button size="sm" variant="outline" onClick={load} disabled={loading} className="h-7" data-testid="setup-refresh">
                    <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
                </Button>
            </div>

            {/* Configuration values */}
            <div className="space-y-1.5 text-xs bg-neutral-50 rounded p-3 border border-neutral-200">
                <Row label="Tenant ID" value={status.tenant_id} onCopy={copy} />
                <Row label="Client ID" value={status.client_id} onCopy={copy} />
                <Row label="Redirect URI" value={status.redirect_uri} onCopy={copy} highlight />
            </div>

            {/* Live status */}
            <div className={`rounded p-3 border ${fullyOk ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`}>
                <div className="flex items-center gap-2 mb-2">
                    {fullyOk ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <AlertTriangle className="w-4 h-4 text-amber-600" />}
                    <p className="font-semibold text-sm">
                        {fullyOk
                            ? t("Alles eingerichtet — du kannst freigegebene Postfächer hinzufügen.", "All set — you can add shared mailboxes.")
                            : t("Setup noch nicht vollständig", "Setup incomplete")}
                    </p>
                </div>
                <ul className="text-xs space-y-1">
                    <li className="flex items-center gap-2">
                        {status.app_only_ok ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> : <XCircle className="w-3.5 h-3.5 text-red-500" />}
                        {t("App-Only Token (Client Credentials)", "App-Only Token (Client Credentials)")}
                    </li>
                    {REQUIRED_APP_ROLES.map((r) => {
                        const has = (status.app_roles || []).includes(r);
                        return (
                            <li key={r} className="flex items-center gap-2">
                                {has ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> : <XCircle className="w-3.5 h-3.5 text-red-500" />}
                                {t(`Application Permission: ${r}`, `Application Permission: ${r}`)}
                            </li>
                        );
                    })}
                    <li className="flex items-center gap-2">
                        {status.test_graph_call?.ok ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> : <XCircle className="w-3.5 h-3.5 text-red-500" />}
                        {t("Live Graph-API Test", "Live Graph-API test")} <span className="text-neutral-500">({status.test_graph_call?.status})</span>
                    </li>
                </ul>
            </div>

            {/* One-click admin consent */}
            {!fullyOk && (
                <div className="rounded p-3 bg-blue-50 border border-blue-200">
                    <p className="text-sm font-semibold mb-1">{t("Lösung in 2 Schritten:", "Solution in 2 steps:")}</p>
                    <ol className="text-xs space-y-2 list-decimal list-inside text-neutral-700 mb-3">
                        <li>
                            {t("Im Azure-Portal die Redirect-URI hinzufügen:", "In the Azure portal, add the Redirect URI:")}
                            <code className="ml-1 bg-white px-1.5 py-0.5 rounded border border-neutral-200 break-all">{status.redirect_uri}</code>
                            <br />
                            <span className="text-neutral-500">
                                {t("Pfad: ", "Path: ")}<a href={`https://portal.azure.com/#blade/Microsoft_AAD_RegisteredApps/ApplicationMenuBlade/Authentication/appId/${status.client_id}`} target="_blank" rel="noreferrer" className="underline text-blue-600">App Registrations → MyTACS-App → Authentication → Add a platform → Web</a>
                            </span>
                        </li>
                        <li>
                            {t("Klicke auf den Admin-Consent-Button (unten) — du wirst zu Microsoft weitergeleitet und kannst alle Berechtigungen mit EINEM Klick erteilen.",
                               "Click the Admin Consent button below — you'll be redirected to Microsoft and can grant all permissions in ONE click.")}
                        </li>
                    </ol>
                    <a
                        href={status.admin_consent_url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-2 px-4 h-10 bg-blue-600 hover:bg-blue-700 text-white rounded font-semibold text-sm transition"
                        data-testid="admin-consent-link"
                    >
                        <ExternalLink className="w-4 h-4" />
                        {t("Admin-Consent jetzt erteilen (öffnet Microsoft-Login)", "Grant admin consent now (opens Microsoft login)")}
                    </a>
                    <p className="text-[11px] text-neutral-500 mt-2">
                        {t('Anschließend hier auf "Aktualisieren" klicken — die grünen Haken sollten erscheinen.',
                           'After granting, click "Refresh" — the green checkmarks should appear.')}
                    </p>
                </div>
            )}

            {status.test_graph_call && !status.test_graph_call.ok && (
                <details className="text-[11px] text-neutral-500">
                    <summary className="cursor-pointer">{t("Graph-API Fehlerdetails", "Graph API error details")}</summary>
                    <pre className="bg-neutral-50 p-2 rounded mt-1 overflow-auto whitespace-pre-wrap">{status.test_graph_call.detail}</pre>
                </details>
            )}
        </div>
    );
}

function Row({ label, value, onCopy, highlight }) {
    return (
        <div className="flex items-start gap-2">
            <span className="font-semibold text-neutral-600 w-28 flex-shrink-0">{label}:</span>
            <code className={`flex-1 break-all px-1.5 py-0.5 rounded ${highlight ? "bg-blue-50 border border-blue-200" : ""}`}>{value || "—"}</code>
            {value && (
                <button onClick={() => onCopy(value)} className="text-neutral-400 hover:text-brand-500 flex-shrink-0" title="Copy">
                    <Copy className="w-3 h-3" />
                </button>
            )}
        </div>
    );
}
